import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import PageLayout, { PageSection } from '../../components/navigation/PageLayout';
import Icon from '../../components/AppIcon';
import { apiClient } from '../../services/api/client';
import { useTenantContext } from '../../contexts/TenantContext';
import InviteUserModal from './components/InviteUserModal';

const ROLE_OPTIONS = [
    { value: 'User', label: 'User', description: 'Basic access to view data' },
    { value: 'Manager', label: 'Manager', description: 'Can manage employees and distributions' },
    { value: 'TenantAdmin', label: 'Administrator', description: 'Full tenant administration access' },
];

const UserManagement = () => {
    const { selectedTenant, isGlobalAdmin, needsTenantSelection } = useTenantContext();
const [activeTab, setActiveTab] = useState('users');
  const [users, setUsers] = useState([]);
    const [invitations, setInvitations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [notification, setNotification] = useState(null);
    const [showInviteModal, setShowInviteModal] = useState(false);
    
    const shouldFetchData = !isGlobalAdmin || selectedTenant;
    
    useEffect(() => {
     if (shouldFetchData) {
fetchUsers();
            fetchInvitations();
        }
    }, [selectedTenant?.id, shouldFetchData]);
    
    const showNotification = (message, type = 'success') => {
        setNotification({ message, type });
        setTimeout(() => setNotification(null), 5000);
    };
    
    const fetchUsers = async () => {
    try {
    const response = await apiClient.get('/users');
   if (response.success) {
 setUsers(response.data || []);
            }
  } catch (err) {
   console.error('Failed to fetch users:', err);
 }
    };
    
    const fetchInvitations = async () => {
        try {
            setLoading(true);
     const response = await apiClient.get('/users/invitations');
     if (response.success) {
 setInvitations(response.data || []);
   }
  } catch (err) {
  setError(err.message || 'Failed to fetch invitations');
        } finally {
     setLoading(false);
     }
    };
    
    const handleInviteSent = () => {
        setShowInviteModal(false);
        fetchInvitations();
        showNotification('Invitation sent successfully!');
    };
    
    const handleResendInvitation = async (invitationId) => {
  try {
      const response = await apiClient.post(`/users/invitations/${invitationId}/resend`);
  if (response.success) {
    showNotification('Invitation resent successfully!');
    fetchInvitations();
   } else {
      showNotification(response.message || 'Failed to resend invitation', 'error');
        }
 } catch (err) {
    showNotification(err.message || 'Failed to resend invitation', 'error');
     }
    };
 
    const handleRevokeInvitation = async (invitationId) => {
   if (!confirm('Are you sure you want to revoke this invitation?')) return;
        
        try {
            const response = await apiClient.post(`/users/invitations/${invitationId}/revoke`);
   if (response.success) {
   showNotification('Invitation revoked successfully!');
  fetchInvitations();
      } else {
    showNotification(response.message || 'Failed to revoke invitation', 'error');
            }
        } catch (err) {
          showNotification(err.message || 'Failed to revoke invitation', 'error');
   }
 };
    
    const handleDeactivateUser = async (userId) => {
   if (!confirm('Are you sure you want to deactivate this user?')) return;
        
        try {
      const response = await apiClient.post(`/users/${userId}/deactivate`);
  if (response.success) {
              showNotification('User deactivated successfully!');
  fetchUsers();
    } else {
       showNotification(response.message || 'Failed to deactivate user', 'error');
  }
} catch (err) {
        showNotification(err.message || 'Failed to deactivate user', 'error');
        }
    };
    
    const pendingInvitations = invitations.filter(i => i.status === 'Pending');
    const expiredInvitations = invitations.filter(i => i.status === 'Expired' || i.isExpired);
    
    const breadcrumbs = [
   { label: 'Dashboard', path: '/dashboard-overview' },
        { label: 'Settings', path: '/settings' },
        { label: 'User Management', path: '/settings/users' },
    ];
    
    // Tenant selection required for global admins
    if (isGlobalAdmin && needsTenantSelection) {
  return (
   <>
       <Helmet><title>User Management - Rocket Tronc</title></Helmet>
           <PageLayout title="User Management" breadcrumbs={breadcrumbs}>
    <div className="card p-12 text-center">
       <div className="w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-6">
    <Icon name="Building2" size={40} className="text-primary" />
   </div>
                 <h2 className="text-2xl font-bold mb-3">Select a Tenant</h2>
   <p className="text-muted-foreground mb-6 max-w-md mx-auto">
         Please select a tenant from the sidebar to manage users.
               </p>
          </div>
 </PageLayout>
            </>
 );
    }
    
  return (
        <>
   <Helmet>
      <title>User Management - {selectedTenant?.name || 'Rocket Tronc'}</title>
    </Helmet>
        
    <PageLayout
    title="User Management"
       description="Manage users and send invitations"
   breadcrumbs={breadcrumbs}
            >
        {/* Notification */}
  {notification && (
       <div className={`fixed top-20 right-4 z-[1300] alert ${
                notification.type === 'error' ? 'alert-error' : 'alert-success'
        } shadow-lg`}>
   <Icon name={notification.type === 'error' ? 'AlertCircle' : 'CheckCircle'} size={20} />
      <span>{notification.message}</span>
    <button onClick={() => setNotification(null)} className="ml-2">
       <Icon name="X" size={16} />
    </button>
   </div>
         )}
             
      {/* Actions */}
       <div className="flex flex-wrap gap-3 mb-6">
   <button className="btn btn-primary" onClick={() => setShowInviteModal(true)}>
    <Icon name="UserPlus" size={18} />
       Invite User
            </button>
   </div>
 
    {/* Tabs */}
        <div className="flex gap-2 mb-6 border-b border-border">
         <button
   className={`px-4 py-2 font-medium border-b-2 transition-colors ${
     activeTab === 'users' 
           ? 'border-primary text-primary' 
           : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
        onClick={() => setActiveTab('users')}
           >
          <Icon name="Users" size={16} className="inline mr-2" />
        Users ({users.length})
     </button>
        <button
                  className={`px-4 py-2 font-medium border-b-2 transition-colors ${
                activeTab === 'invitations' 
     ? 'border-primary text-primary' 
      : 'border-transparent text-muted-foreground hover:text-foreground'
         }`}
   onClick={() => setActiveTab('invitations')}
       >
                <Icon name="Mail" size={16} className="inline mr-2" />
   Invitations ({pendingInvitations.length} pending)
         </button>
       </div>
      
  {/* Users Tab */}
          {activeTab === 'users' && (
<PageSection title="Active Users">
         {users.length === 0 ? (
         <div className="card p-8 text-center">
   <Icon name="Users" size={48} className="mx-auto mb-4 text-muted-foreground" />
    <p className="text-muted-foreground mb-4">No users found</p>
         <button className="btn btn-primary btn-sm" onClick={() => setShowInviteModal(true)}>
 <Icon name="UserPlus" size={16} />
 Invite First User
 </button>
         </div>
        ) : (
         <div className="card overflow-hidden">
    <table className="w-full">
      <thead className="bg-muted/50">
         <tr>
       <th className="text-left p-4 font-medium">User</th>
            <th className="text-left p-4 font-medium">Email</th>
      <th className="text-left p-4 font-medium">Role</th>
   <th className="text-left p-4 font-medium">Status</th>
       <th className="text-left p-4 font-medium">Last Login</th>
    <th className="text-right p-4 font-medium">Actions</th>
    </tr>
     </thead>
     <tbody className="divide-y divide-border">
         {users.map((user) => (
                <tr key={user.id} className="hover:bg-muted/30">
        <td className="p-4">
  <div className="flex items-center gap-3">
       <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
        <span className="text-primary font-semibold">
        {user.firstName?.[0]}{user.lastName?.[0]}
   </span>
 </div>
         <div>
           <p className="font-medium">{user.fullName}</p>
      </div>
             </div>
      </td>
     <td className="p-4 text-muted-foreground">{user.email}</td>
 <td className="p-4">
    <span className={`badge ${
   user.role === 'GlobalAdmin' ? 'badge-error' :
     user.role === 'TenantAdmin' ? 'badge-primary' :
user.role === 'Manager' ? 'badge-secondary' :
      'badge-neutral'
         }`}>
                {user.role}
    </span>
      </td>
   <td className="p-4">
     <span className={`badge ${user.isActive ? 'badge-success' : 'badge-neutral'}`}>
       {user.isActive ? 'Active' : 'Inactive'}
    </span>
         </td>
        <td className="p-4 text-muted-foreground text-sm">
     {user.lastLoginOn 
          ? new Date(user.lastLoginOn).toLocaleDateString()
 : 'Never'
           }
           </td>
            <td className="p-4 text-right">
    {user.isActive && (
 <button
 className="p-2 hover:bg-error/10 rounded-md text-error"
   onClick={() => handleDeactivateUser(user.id)}
       title="Deactivate user"
   >
  <Icon name="UserMinus" size={16} />
                  </button>
    )}
      </td>
               </tr>
        ))}
          </tbody>
        </table>
             </div>
            )}
 </PageSection>
     )}
        
       {/* Invitations Tab */}
 {activeTab === 'invitations' && (
      <>
           {/* Pending Invitations */}
     <PageSection title="Pending Invitations">
 {pendingInvitations.length === 0 ? (
        <div className="card p-8 text-center">
     <Icon name="Mail" size={48} className="mx-auto mb-4 text-muted-foreground" />
 <p className="text-muted-foreground mb-4">No pending invitations</p>
          <button className="btn btn-primary btn-sm" onClick={() => setShowInviteModal(true)}>
         <Icon name="UserPlus" size={16} />
   Send Invitation
 </button>
         </div>
         ) : (
             <div className="grid gap-4">
    {pendingInvitations.map((invitation) => (
        <div key={invitation.id} className="card p-4">
        <div className="flex items-center justify-between">
    <div className="flex items-center gap-4">
       <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
      <Icon name="Mail" size={20} className="text-primary" />
          </div>
 <div>
            <p className="font-semibold">{invitation.fullName}</p>
   <p className="text-sm text-muted-foreground">{invitation.email}</p>
         <div className="flex items-center gap-2 mt-1">
  <span className="badge badge-neutral text-xs">{invitation.role}</span>
              <span className="text-xs text-muted-foreground">
      Invited by {invitation.invitedBy}
         </span>
            </div>
         </div>
          </div>
         <div className="flex items-center gap-2">
  <div className="text-right mr-4">
     <p className="text-xs text-muted-foreground">Expires</p>
        <p className={`text-sm ${invitation.isExpired ? 'text-error' : ''}`}>
        {new Date(invitation.expiresOn).toLocaleDateString()}
 </p>
           {invitation.emailSentCount > 1 && (
<p className="text-xs text-muted-foreground">
 Sent {invitation.emailSentCount} times
             </p>
     )}
        </div>
         <button
     className="btn btn-outline btn-sm"
   onClick={() => handleResendInvitation(invitation.id)}
        title="Resend invitation"
    >
          <Icon name="RefreshCw" size={14} />
                 Resend
       </button>
     <button
 className="btn btn-outline btn-sm text-error hover:bg-error/10"
              onClick={() => handleRevokeInvitation(invitation.id)}
          title="Revoke invitation"
        >
      <Icon name="X" size={14} />
         Revoke
   </button>
      </div>
                 </div>
       </div>
 ))}
       </div>
            )}
              </PageSection>
       
      {/* Invitation History */}
     {invitations.filter(i => i.status !== 'Pending').length > 0 && (
    <PageSection title="Invitation History" className="mt-6">
          <div className="card overflow-hidden">
         <table className="w-full">
    <thead className="bg-muted/50">
       <tr>
   <th className="text-left p-4 font-medium">Invitee</th>
     <th className="text-left p-4 font-medium">Email</th>
       <th className="text-left p-4 font-medium">Status</th>
   <th className="text-left p-4 font-medium">Date</th>
     </tr>
      </thead>
    <tbody className="divide-y divide-border">
        {invitations.filter(i => i.status !== 'Pending').map((invitation) => (
             <tr key={invitation.id} className="hover:bg-muted/30">
 <td className="p-4 font-medium">{invitation.fullName}</td>
     <td className="p-4 text-muted-foreground">{invitation.email}</td>
         <td className="p-4">
         <span className={`badge ${
          invitation.status === 'Accepted' ? 'badge-success' :
   invitation.status === 'Expired' ? 'badge-warning' :
invitation.status === 'Revoked' ? 'badge-error' :
       'badge-neutral'
                }`}>
     {invitation.status}
            </span>
       </td>
                <td className="p-4 text-muted-foreground text-sm">
 {invitation.acceptedOn 
    ? new Date(invitation.acceptedOn).toLocaleDateString()
         : new Date(invitation.createdOn).toLocaleDateString()
      }
   </td>
   </tr>
             ))}
            </tbody>
   </table>
   </div>
             </PageSection>
  )}
       </>
         )}
   
        {/* Invite User Modal */}
          {showInviteModal && (
        <InviteUserModal
         onClose={() => setShowInviteModal(false)}
 onSuccess={handleInviteSent}
      roleOptions={ROLE_OPTIONS}
          />
       )}
  </PageLayout>
  </>
    );
};

export default UserManagement;
